import { useEffect, useState } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, type FieldErrors, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import DropDown from "@/components/forms/inputs/dropDown";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import RoundedContentBox from "@/components/general/roundedContentBox";
import TinyMceEditor from "@/components/tinymce/tinymce";
import { RootState } from "@/components/state/store";
import styles from "@/routes/lms/lmsCreateCourse.module.css";
import useConfirmOnLeave from "@/helper/customHooks/useConfirmOnLeave";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import LmsBackButton from "@/components/lms/lmsBackButton";
import LmsIntervalFields from "@/components/lms/lmsIntervalFields";
import { type LmsCourseOverviewLocationState } from "@/routes/lms/lmsCourseCreationFeedback";
import {
  buildLmsCourseSubmitData,
  COURSE_CREATE_PERMISSION,
  createPromiseRequest,
  getLmsCourseFieldErrorMessage,
  getDefaultCourseFormValues,
  getEffectiveLmsMode,
  getLmsCourseOverviewRoute,
  getLmsCoursesRoute,
  isValidLmsIntervalValue,
  loadLmsCourseCategoryOptions,
  LmsCourseFormValues,
  LmsDropDownOption,
  LmsMode,
  shouldBlockLmsForSubdomain,
} from "@/components/lms/lms.utils";

interface Props {
  requiredMode?: LmsMode;
}

type CourseFormValues = LmsCourseFormValues;
type DropDownOption = LmsDropDownOption;
type CourseFieldWithValidation =
  | "title"
  | "category"
  | "completionIntervalInDays"
  | "renewalCooldownInDays"
  | "validityIntervalInDays";

const courseFieldValidationOrder: CourseFieldWithValidation[] = [
  "title",
  "category",
  "completionIntervalInDays",
  "renewalCooldownInDays",
  "validityIntervalInDays",
];

export default function LmsCreateCourse({ requiredMode }: Props) {
  const { t } = useTranslation();
  usePageHeading(t("create_course"));
  const navigate = useNavigate();
  const loginState = useSelector((state: RootState) => state.login);
  const { handleError, errorModal } = useErrorModal(t("create_course"));
  const [categoryOptions, setCategoryOptions] = useState<DropDownOption[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors, touchedFields, isDirty, isSubmitted },
    reset,
  } = useForm<CourseFormValues>({
    defaultValues: getDefaultCourseFormValues(),
    mode: "onChange",
    shouldFocusError: false,
  });
  const { focusFirstInvalidField, registerValidationTarget } =
    useFocusFirstInvalidField<CourseFormValues>({
      fieldOrder: courseFieldValidationOrder,
    });

  const hasUnsavedChanges = !isSubmitting && isDirty;
  useConfirmOnLeave(hasUnsavedChanges, "unsaved_formChanges_confirm");

  const subdomain = window.location.hostname.split(".")[0];
  const shouldBlockLms = shouldBlockLmsForSubdomain({
    subdomain,
    permissions: loginState.permissions,
  });
  const effectiveMode = getEffectiveLmsMode({
    subdomain,
    permissions: loginState.permissions,
  });
  const canCreateCourse = loginState.permissions.includes(
    COURSE_CREATE_PERMISSION
  );

  const getFieldErrorMessage = (fieldName: keyof LmsCourseFormValues) =>
    getLmsCourseFieldErrorMessage(
      fieldName,
      errors,
      touchedFields,
      isSubmitted
    );

  useEffect(() => {
    if (
      shouldBlockLms ||
      !canCreateCourse ||
      (requiredMode && effectiveMode !== requiredMode)
    ) {
      return;
    }

    reset(getDefaultCourseFormValues());
    handleError(null);
    setIsSubmitting(false);
    setIsLoadingCategories(true);

    loadLmsCourseCategoryOptions({ shouldNotImpersonate: true })
      .then(setCategoryOptions)
      .catch((requestError: any) => {
        handleError(requestError);
        setCategoryOptions([]);
      })
      .finally(() => {
        setIsLoadingCategories(false);
      });
  }, [canCreateCourse, effectiveMode, requiredMode, reset, shouldBlockLms, t]);

  const handleCancel = () => {
    if (isSubmitting) {
      return;
    }
    navigate(getLmsCoursesRoute("administrator"));
  };

  const handleInvalidFormSubmit = (
    fieldErrors: FieldErrors<CourseFormValues>
  ) => {
    focusFirstInvalidField(fieldErrors);
  };

  const handleFormSubmit = async (values: CourseFormValues) => {
    if (!canCreateCourse) {
      return;
    }

    setIsSubmitting(true);

    try {
      const createResponse = await createPromiseRequest({
        entity: "course",
        method: "post",
        config: {
          headers: { "Content-Type": "application/json" },
        },
        submitData: buildLmsCourseSubmitData(values),
      });

      const courseId = String(createResponse?.data?.id ?? "");
      if (!courseId) {
        setIsSubmitting(false);
        return;
      }

      const locationState: LmsCourseOverviewLocationState = {
        courseCreationFeedback: { uploadErrors: [] },
      };
      navigate(getLmsCourseOverviewRoute("administrator", courseId), {
        state: locationState,
      });
    } catch (requestError) {
      handleError(requestError);
      setIsSubmitting(false);
    }
  };

  const formValues = watch();
  const certificateExpirable = Boolean(formValues.certificateExpirable);

  useEffect(() => {
    if (!certificateExpirable) {
      clearErrors("validityIntervalInDays");
    }
  }, [certificateExpirable, clearErrors]);

  const submitDisabled = isSubmitting;

  if (shouldBlockLms) {
    return <Navigate to="/" replace />;
  }

  if (requiredMode && effectiveMode !== requiredMode) {
    return <Navigate to={getLmsCoursesRoute(effectiveMode)} replace />;
  }

  if (effectiveMode === "administrator" && !canCreateCourse) {
    return <Navigate to={getLmsCoursesRoute("administrator")} replace />;
  }

  return (
    <Grid container rowSpacing={2}>
      <Grid item xs={12}>
        <RoundedContentBox>
          <div className={styles.headerContainer}>
            <LmsBackButton disabled={isSubmitting} />
          </div>
        </RoundedContentBox>
      </Grid>

      <Grid item xs={12}>
        <form
          noValidate
          onSubmit={handleSubmit(handleFormSubmit, handleInvalidFormSubmit)}
        >
          <div className={styles.dialogContent}>
            <div className={styles.section}>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  data-testid="lms-course-title-field"
                  ref={registerValidationTarget("title")}
                >
                  <Controller
                    name="title"
                    control={control}
                    rules={{
                      required: t("field_required"),
                      validate: (value) =>
                        Boolean(value.trim()) || t("field_required"),
                    }}
                    render={({ field }) => (
                      <>
                        <Typography>
                          {t("course_title")}
                          <span className={styles.requiredAsterisk}>*</span>
                        </Typography>
                        <DefaultInput
                          {...field}
                          name="lms-course-title"
                          aria-label={t("course_title")}
                        />
                        {errors.title?.message && (
                          <div className={styles.fieldErrorText}>
                            {errors.title.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  data-testid="lms-course-category-field"
                  ref={registerValidationTarget("category")}
                >
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: t("field_required") }}
                    render={({ field }) => (
                      <>
                        <DropDown
                          options={categoryOptions}
                          onChange={field.onChange}
                          externalValue={field.value}
                          label={t("category")}
                          mandatory={true}
                          name="course-category-page"
                          showLoadingIndicator={isLoadingCategories}
                        />
                        {getFieldErrorMessage("category") && (
                          <div className={styles.fieldErrorText}>
                            {getFieldErrorMessage("category")}
                          </div>
                        )}
                      </>
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  className={styles.checkboxFieldGridItem}
                >
                  <Controller
                    name="showCourseDurationOnCertificate"
                    control={control}
                    render={({ field }) => (
                      <div className={styles.toggleFieldRow}>
                        <Typography>
                          {t("show_course_duration_on_certificate")}
                          <span className={styles.requiredAsterisk}>*</span>
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          color="secondary"
                          value={field.value ? "yes" : "no"}
                          onChange={(_, value) => {
                            if (!value) return;
                            field.onChange(value === "yes");
                          }}
                          sx={{
                            height: "56px",
                            "& .MuiToggleButton-root": {
                              height: "56px",
                              textTransform: "none",
                              fontWeight: 600,
                            },
                          }}
                        >
                          <ToggleButton value="yes">{t("yes")}</ToggleButton>
                          <ToggleButton value="no">{t("no")}</ToggleButton>
                        </ToggleButtonGroup>
                      </div>
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  className={styles.checkboxFieldGridItem}
                >
                  <Controller
                    name="isPrivate"
                    control={control}
                    render={({ field }) => (
                      <div className={styles.toggleFieldRow}>
                        <Typography>
                          {t("private_course")}
                          <span className={styles.requiredAsterisk}>*</span>
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          color="secondary"
                          value={field.value ? "yes" : "no"}
                          onChange={(_, value) => {
                            if (!value) return;
                            field.onChange(value === "yes");
                          }}
                          sx={{
                            height: "56px",
                            "& .MuiToggleButton-root": {
                              height: "56px",
                              textTransform: "none",
                              fontWeight: 600,
                            },
                          }}
                        >
                          <ToggleButton value="yes">{t("yes")}</ToggleButton>
                          <ToggleButton value="no">{t("no")}</ToggleButton>
                        </ToggleButtonGroup>
                      </div>
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  className={styles.checkboxFieldGridItem}
                >
                  <Controller
                    name="certificateExpirable"
                    control={control}
                    render={({ field }) => (
                      <div className={styles.toggleFieldRow}>
                        <Typography>
                          {t("course_certificate_expirable")}
                          <span className={styles.requiredAsterisk}>*</span>
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          color="secondary"
                          value={field.value ? "yes" : "no"}
                          onChange={(_, value) => {
                            if (!value) return;
                            field.onChange(value === "yes");
                          }}
                          sx={{
                            height: "56px",
                            "& .MuiToggleButton-root": {
                              height: "56px",
                              textTransform: "none",
                              fontWeight: 600,
                            },
                          }}
                        >
                          <ToggleButton value="yes">{t("yes")}</ToggleButton>
                          <ToggleButton value="no">{t("no")}</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="body2">
                          {t("course_recurrence_help")}
                        </Typography>
                      </div>
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  data-testid="lms-course-completion-interval-field"
                >
                  <Controller
                    name="completionIntervalInDays"
                    control={control}
                    rules={{
                      required: t("field_required"),
                      validate: (value) =>
                        Number(value) > 0 ||
                        t("field_must_be_greater_than_zero"),
                    }}
                    render={({ field }) => (
                      <>
                        <div className={styles.fieldHeaderWithTooltip}>
                          <Typography>
                            {t("completion_interval_in_days")}
                            <span className={styles.requiredAsterisk}>*</span>
                          </Typography>
                          <Tooltip
                            title={t("completion_interval_in_days_help")}
                            placement="left"
                            arrow
                          >
                            <GenericButton
                              variant="icon"
                              color="iconBase"
                              size="small"
                              className={styles.helpIconButton}
                            >
                              <HelpOutlineIcon fontSize="small" />
                            </GenericButton>
                          </Tooltip>
                        </div>
                        <div
                          ref={registerValidationTarget(
                            "completionIntervalInDays"
                          )}
                        >
                          <DefaultInput
                            {...field}
                            name="lms-course-completion-interval"
                            type="number"
                            min={1}
                          />
                        </div>
                        {errors.completionIntervalInDays?.message && (
                          <div className={styles.fieldErrorText}>
                            {errors.completionIntervalInDays.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </Grid>
                {certificateExpirable && (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    ref={registerValidationTarget("renewalCooldownInDays")}
                  >
                    <Controller
                      name="renewalCooldownInDays"
                      control={control}
                      rules={{
                        validate: (value) =>
                          (Boolean(value.trim()) &&
                            isValidLmsIntervalValue(value)) ||
                          t("field_must_be_zero_or_greater"),
                      }}
                      render={({ field }) => (
                        <LmsIntervalFields
                          value={field.value}
                          onValueChange={field.onChange}
                          onValueBlur={field.onBlur}
                          valueRef={field.ref}
                          disabled={isSubmitting}
                          labelKey="renewal_cooldown_in_days"
                          helpKey="renewal_cooldown_in_days_help"
                          errorMessage={errors.renewalCooldownInDays?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
                {certificateExpirable && (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    data-testid="lms-course-validity-interval-field"
                  >
                    <Controller
                      name="validityIntervalInDays"
                      control={control}
                      rules={{
                        validate: (value) =>
                          !certificateExpirable ||
                          (Boolean(value.trim()) &&
                            isValidLmsIntervalValue(value)) ||
                          t("field_must_be_zero_or_greater"),
                      }}
                      render={({ field: valueField }) => (
                        <div
                          ref={registerValidationTarget(
                            "validityIntervalInDays"
                          )}
                        >
                          <LmsIntervalFields
                            value={valueField.value}
                            onValueChange={valueField.onChange}
                            onValueBlur={valueField.onBlur}
                            valueRef={valueField.ref}
                            disabled={isSubmitting}
                            labelKey="validity_interval_in_days"
                            helpKey="course_validity_interval_help"
                            errorMessage={
                              errors.validityIntervalInDays?.message
                            }
                          />
                        </div>
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <>
                        <div className={styles.fieldHeaderWithTooltip}>
                          <Typography>{t("course_description")}</Typography>
                        </div>
                        <TinyMceEditor
                          value={field.value}
                          onChange={field.onChange}
                          showFooter={false}
                          height={260}
                        />
                      </>
                    )}
                  />
                </Grid>
              </Grid>
            </div>
          </div>

          <div className={styles.ctaButtonsWrapper}>
            <GenericButton
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {t("cancel")}
            </GenericButton>
            <GenericButton
              type="submit"
              disabled={submitDisabled}
              data-testid="lms-create-course-submit-button"
            >
              {t("create_course")}
            </GenericButton>
          </div>
        </form>
      </Grid>

      {errorModal}
    </Grid>
  );
}
